import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { PanelContainer, ToolbarButton } from '../../ui/common';
import { getFocusable } from '../ui/Modal';
import { useTranslation } from '../../translations';

/**
 * WSPÓLNY CHROM OBU KONSTRUKTORÓW (umowy i oferty).
 *
 * Obie strony robią dla klienta to samo: układasz dokument, oglądasz go na kartce, zapisujesz
 * szkic, publikujesz, w razie czego wracasz do poprzedniej wersji. Skoro przepływ jest ten sam,
 * pasek statusu, historia wersji, okno potwierdzenia publikacji i okno podglądu PDF nie mogą być
 * dwiema osobnymi implementacjami — inaczej po pierwszej poprawce w jednej z nich strony zaczną
 * wyglądać jak dwa różne produkty. Definicje pochodzą z konstruktora umowy; konstruktor oferty
 * dostał je 2026-08-21 razem z cyklem szkic/publikacja (migracja 041).
 *
 * Tu trafia WYŁĄCZNIE to, co jest wspólne. Rzeczy specyficzne (karta sekcji, pigułki kroków,
 * grupy pól oferty) zostają u siebie.
 */

// ==== StatusStrip ====
// ── Status szablonu ─────────────────────────────────────────────────────────
/* Dawniej pelnej szerokosci baner w kolorze stanu (zielony/pomaranczowy, ramka 2px) —
   krzyczal glosniej niz tresc, ktora opisywal. Teraz kolor niesie sama odznaka. */
const StatusStrip = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
  padding: var(--sp-3) var(--sp-4);
  margin-bottom: var(--sp-4);
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
`;

// ==== StatusText ====
const StatusText = styled.span`
  flex: 1 1 240px;
  min-width: 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

// ==== HistoryBox ====
const HistoryBox = styled.div`
  padding: var(--sp-4);
  margin-bottom: var(--sp-4);
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
`;

// ==== HistoryHead ====
const HistoryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
  font-size: var(--fs-sm);
  font-weight: 700;
  color: var(--text);
`;

// ==== VersionCard ====
const VersionCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  margin-bottom: var(--sp-2);
  font-size: var(--fs-sm);
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);

  &:last-child { margin-bottom: 0; }
`;

// ==== VersionDate ====
const VersionDate = styled.span`
  margin-left: var(--sp-2);
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

// ==== SheetNote ====
/* Podpis pod kartką — liczba stron i uczciwe zastrzeżenie, że podział jest orientacyjny. */
const SheetNote = styled.div`
  margin-top: var(--sp-2);
  text-align: center;
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

// ==== PublishButton ====
// ── Przyciski ───────────────────────────────────────────────────────────────
/* Publikacja to jedyna akcja, ktora zmienia szablon dla calej organizacji — dlatego
   jako jedyna ma kolor stanu, a nie akcentu. */
const PublishButton = styled(ToolbarButton)`
  background: var(--success);
  border-color: transparent;
  color: var(--primary-contrast);

  &:hover:not(:disabled) {
    background: var(--success);
    border-color: transparent;
    filter: brightness(0.93);
  }
`;

// ==== SmallButton ====
const SmallButton = styled.button`
  /* Etykieta ma zostac cala; wiersz obok jest elastyczny i bez tego przycisk oddaje szerokosc. */
  flex-shrink: 0;
  padding: var(--sp-1) var(--sp-3);
  border-radius: var(--r-sm);
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: var(--text);
  font-family: inherit;
  font-size: var(--fs-xs);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease);

  &:hover:not(:disabled) { background: var(--hover-bg); border-color: var(--border); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ==== ModalOverlay ====
// ── Okna modalne ────────────────────────────────────────────────────────────
/*
 * Okna „Publikuj szablon?” i „Podgląd PDF” w OBU konstruktorach (ponowny test odbiorczy
 * 11.10.2026). Były ostylowanymi divami: bez role="dialog" i aria-modal, bez „×”, fokus zostawał
 * na przycisku pod nakładką (Tab szedł do strony za oknem), a po zamknięciu lądował na <body>.
 * Esc z ramki PDF nie działał, bo zdarzenia klawiatury z dokumentu ramki nie docierają do strony.
 *
 * Składniki zachowują DOTYCHCZASOWE API (ModalOverlay z `onClick` = zamknij, ModalBox,
 * ModalTitle, PdfModalBox, PdfFrame), więc obie strony dostają poprawkę bez przepisywania.
 * Zachowanie jest jak we wspólnym components/ui/Modal.js: okno w portalu, fokus wchodzi do okna
 * i wraca na przycisk, który je otworzył, Tab krąży w oknie, a fokus, który ucieknie z okna (np.
 * Tabem z ramki PDF), wraca do niego. Tło przewijania nie jest blokowane, jak dotąd.
 */
const OknoKontekst = createContext(null);

const TloOkna = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-4);
  z-index: var(--z-overlay);
`;

/*
 * `powrotFokusu` (ref przycisku, który otwiera okno): fokus wraca na niego po zamknięciu, nawet
 * gdy w chwili otwarcia stał już na <body>. Tak jest przy podglądzie PDF: przycisk jest wyłączony,
 * dopóki serwer generuje dokument, a przeglądarka zabiera fokus wyłączonemu przyciskowi, więc
 * okno zapamiętywało <body> i tam oddawało fokus (sprawdzenie 12.10.2026). Przy zamknięciu
 * przycisk jest już włączony.
 */
function ModalOverlay({ onClick, powrotFokusu, children }) {
  const zamknijRef = useRef(onClick);
  zamknijRef.current = onClick;
  const powrotRef = useRef(powrotFokusu);
  powrotRef.current = powrotFokusu;
  const oknoRef = useRef(null);
  const gestZTlaRef = useRef(false);
  const id = useId();

  const zamknij = useCallback(() => {
    if (typeof zamknijRef.current === 'function') zamknijRef.current();
  }, []);
  const ustawOkno = useCallback((el) => { oknoRef.current = el; }, []);

  useEffect(() => {
    const otwierajacy = () => (powrotRef.current && powrotRef.current.current) || null;
    const poprzednio = otwierajacy() || document.activeElement;
    const okno = oknoRef.current;
    if (okno) (getFocusable(okno)[0] || okno).focus();

    // Okno ze wspólnego Modal.js otwarte NAD tym (np. potwierdzenie) samo obsługuje Esc i fokus.
    const nadNimInneOkno = () => !!document.querySelector('[data-modal-backdrop]');
    const naKlawisz = (e) => {
      if (e.key !== 'Escape' || nadNimInneOkno()) return;
      zamknij();
    };
    const naFokus = (e) => {
      const el = oknoRef.current;
      if (!el || el.contains(e.target) || nadNimInneOkno()) return;
      (getFocusable(el)[0] || el).focus();
    };
    document.addEventListener('keydown', naKlawisz);
    document.addEventListener('focusin', naFokus);
    return () => {
      document.removeEventListener('keydown', naKlawisz);
      document.removeEventListener('focusin', naFokus);
      // Przycisk z refa czytany przy zamknięciu: strona mogła go w międzyczasie przerysować.
      const cel = otwierajacy() || poprzednio;
      if (cel && typeof cel.focus === 'function' && document.contains(cel)) {
        cel.focus();
      }
    };
  }, [zamknij]);

  const kontekst = useMemo(() => ({ zamknij, ustawOkno, idTytulu: `${id}-tytul` }), [zamknij, ustawOkno, id]);

  return ReactDOM.createPortal(
    <OknoKontekst.Provider value={kontekst}>
      <TloOkna
        onMouseDown={(e) => { gestZTlaRef.current = e.target === e.currentTarget; }}
        onClick={(e) => {
          // Zamyka tylko klik, który zaczął się i skończył na tle: zaznaczanie tekstu w oknie
          // zakończone poza nim nie zamyka okna.
          const zTla = gestZTlaRef.current;
          gestZTlaRef.current = false;
          if (zTla && e.target === e.currentTarget) zamknij();
        }}
      >
        {children}
      </TloOkna>
    </OknoKontekst.Provider>,
    document.body
  );
}

// Tab krąży w oknie (pierwszy ↔ ostatni element), jak w components/ui/Modal.js.
function trzymajTab(e, okno) {
  if (e.key !== 'Tab' || !okno) return;
  const elementy = getFocusable(okno);
  if (elementy.length === 0) { e.preventDefault(); return; }
  const pierwszy = elementy[0];
  const ostatni = elementy[elementy.length - 1];
  const aktywny = document.activeElement;
  if (e.shiftKey && (aktywny === pierwszy || aktywny === okno)) {
    e.preventDefault();
    ostatni.focus();
  } else if (!e.shiftKey && aktywny === ostatni) {
    e.preventDefault();
    pierwszy.focus();
  }
}

function useOkno(onKeyDown) {
  const ctx = useContext(OknoKontekst);
  const lokalnyRef = useRef(null);
  const ref = useCallback((el) => {
    lokalnyRef.current = el;
    if (ctx) ctx.ustawOkno(el);
  }, [ctx]);
  const naKlawisz = (e) => {
    trzymajTab(e, lokalnyRef.current);
    if (onKeyDown) onKeyDown(e);
  };
  return { ctx, ref, naKlawisz };
}

// ==== ModalBox ====
const ModalBoxStyl = styled.div`
  position: relative;
  background: var(--card-bg);
  border-radius: var(--r-lg);
  padding: var(--sp-6);
  max-width: 520px;
  width: 100%;
  max-height: calc(100vh - var(--sp-8));
  overflow-y: auto;
  box-sizing: border-box;
  box-shadow: var(--shadow-lg);

  &:focus { outline: none; }
`;

const PrzyciskZamkniecia = styled.button`
  position: absolute;
  top: var(--sp-3);
  right: var(--sp-3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-1) var(--sp-2);
  background: transparent;
  border: none;
  border-radius: var(--r-sm);
  color: var(--text-muted);
  font-size: var(--fs-xl);
  line-height: 1;
  cursor: pointer;

  &:hover { background: var(--hover-bg); color: var(--text); }
`;

function ModalBox({ children, closeLabel, onKeyDown, ...reszta }) {
  const { t } = useTranslation();
  const { ctx, ref, naKlawisz } = useOkno(onKeyDown);
  return (
    <ModalBoxStyl
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ctx ? ctx.idTytulu : undefined}
      tabIndex={-1}
      onKeyDown={naKlawisz}
      {...reszta}
    >
      {ctx && (
        <PrzyciskZamkniecia type="button" onClick={ctx.zamknij} aria-label={closeLabel || t('common.close')} data-cy="builder-modal-close">
          <span aria-hidden="true">&times;</span>
        </PrzyciskZamkniecia>
      )}
      {children}
    </ModalBoxStyl>
  );
}

// ==== ModalTitle ====
const ModalTitleStyl = styled.h2`
  margin: 0 0 var(--sp-3) 0;
  /* Miejsce na „×” w prawym górnym rogu okna. */
  padding-right: var(--sp-8);
  font-size: var(--fs-lg);
  font-weight: 700;
  color: var(--text);
`;

function ModalTitle(props) {
  const ctx = useContext(OknoKontekst);
  return <ModalTitleStyl id={ctx ? ctx.idTytulu : undefined} {...props} />;
}

// ==== ModalText ====
const ModalText = styled.p`
  margin: 0 0 var(--sp-5) 0;
  font-size: var(--fs-base);
  color: var(--text-muted);
  line-height: 1.55;
`;

// ==== ModalActions ====
const ModalActions = styled.div`
  display: flex;
  gap: var(--sp-2);
  justify-content: flex-end;
  margin-top: var(--sp-4);
`;

// ==== PdfModalBox ====
/* Chrom podgladu PDF ma STALE kolory — tlo modala zostaje biale niezaleznie od motywu
   panelu (jak prawdziwy podglad PDF), wiec tekst i ramka tez musza byc stale, inaczej
   w trybie ciemnym `var(--text)` dalby jasny, nieczytelny napis na bialym tle. */
const PdfModalBoxStyl = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 92%;
  max-width: 980px;
  height: 90vh;
  background: #fff;
  border-radius: var(--r-lg);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  &:focus { outline: none; }
`;

/* Okno podglądu PDF ma własny, widoczny przycisk „Zamknij” w nagłówku, więc bez „×”. Nazwę
   okna daje `aria-label` od strony, a bez niego tytuł ramki z dokumentem (PdfFrame). */
function PdfModalBox({ children, onKeyDown, 'aria-label': ariaLabel, ...reszta }) {
  const { ctx, ref, naKlawisz } = useOkno(onKeyDown);
  return (
    <PdfModalBoxStyl
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={!ariaLabel && ctx ? ctx.idTytulu : undefined}
      tabIndex={-1}
      onKeyDown={naKlawisz}
      {...reszta}
    >
      {children}
    </PdfModalBoxStyl>
  );
}

// ==== PdfModalHead ====
const PdfModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  border-bottom: 1px solid #e5e7eb;
  color: #111827;
  font-size: var(--fs-base);
  font-weight: 700;
`;

// ==== PdfDownloadLink ====
const PdfDownloadLink = styled.a`
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--r-md);
  background: var(--primary);
  color: var(--primary-contrast);
  font-size: var(--fs-sm);
  font-weight: 600;
  text-decoration: none;
`;

// ==== PdfFrame ====
const PdfFrameStyl = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  /* tlo wczytywania PDF w iframe — stale, jak natywna przegladarka PDF */
  background: #525659;
`;

function PdfFrame(props) {
  const ctx = useContext(OknoKontekst);
  return <PdfFrameStyl id={ctx ? ctx.idTytulu : undefined} {...props} />;
}

// ==== PdfCloseButton ====
/*
 * „Zamknij” okna podglądu PDF. Esc zamyka okno tylko wtedy, gdy fokus stoi poza dokumentem:
 * wbudowana przeglądarka PDF w ramce działa jak osobna strona i jej klawisze do panelu nie
 * docierają (ograniczenie przeglądarki, nie do obejścia nasłuchem). Dlatego przycisk mówi
 * „Zamknij (Esc)” wyłącznie wtedy, gdy Esc naprawdę zadziała, a po kliknięciu w dokument mówi
 * samo „Zamknij” (sprawdzenie 12.10.2026). Oba napisy zajmują to samo miejsce, więc przycisk nie
 * zmienia szerokości, a ukryty napis (visibility) nie wchodzi do nazwy dostępnej.
 */
const NapisyZamkniecia = styled.span`
  display: inline-grid;
  & > span { grid-area: 1 / 1; text-align: center; }
`;

const fokusWRamce = () => {
  const el = typeof document !== 'undefined' ? document.activeElement : null;
  return !!el && el.tagName === 'IFRAME';
};

function PdfCloseButton({ onClick, ...reszta }) {
  const { t } = useTranslation();
  const [wRamce, setWRamce] = useState(fokusWRamce);
  useEffect(() => {
    const sprawdz = () => setWRamce(fokusWRamce());
    // Kliknięcie w dokument: strona traci fokus (blur na window), a aktywnym elementem staje się
    // ramka dopiero chwilę później.
    let zegar = null;
    const poUtracie = () => { clearTimeout(zegar); zegar = setTimeout(sprawdz, 0); };
    window.addEventListener('blur', poUtracie);
    window.addEventListener('focus', sprawdz);
    document.addEventListener('focusin', sprawdz);
    return () => {
      clearTimeout(zegar);
      window.removeEventListener('blur', poUtracie);
      window.removeEventListener('focus', sprawdz);
      document.removeEventListener('focusin', sprawdz);
    };
  }, []);
  return (
    <ToolbarButton
      type="button"
      onClick={onClick}
      aria-keyshortcuts={wRamce ? undefined : 'Escape'}
      data-cy="pdf-modal-close"
      {...reszta}
    >
      <NapisyZamkniecia>
        <span style={{ visibility: wRamce ? 'hidden' : 'visible' }}>{t('builderChrome.closeWithEsc')}</span>
        <span style={{ visibility: wRamce ? 'visible' : 'hidden' }}>{t('common.close')}</span>
      </NapisyZamkniecia>
    </ToolbarButton>
  );
}


// ── SZKIELET STRONY ─────────────────────────────────────────────────────────
// Oba konstruktory mają ten sam układ: przyklejony pasek akcji, dwie kolumny
// (edycja / podgląd), przyklejony podgląd na szerokim ekranie. Trzymanie tego
// w jednym miejscu jest jedynym sposobem, żeby „taki sam wygląd" wynikał z kodu,
// a nie z tego, że ktoś pamiętał o poprawieniu drugiej strony.

const Container = styled(PanelContainer)`
  /* PanelContainer przycina zawartosc (overflow: hidden), a przyciety przodek WYLACZA
     position: sticky u potomkow. Ta strona opiera na nim pasek akcji i podglad, wiec
     przyciecia nie uzywa (nie ma tu nic, co wystawaloby poza kontener). */
  overflow: visible;
`;

// ── Pasek akcji (przyklejony) ───────────────────────────────────────────────
const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
  padding: var(--sp-4) 0 var(--sp-3);
  margin-bottom: var(--sp-4);
  background: var(--panel-bg);
  border-bottom: 1px solid var(--input-border);

  /* Na telefonie gorna krawedz nalezy do przycisku menu (App.js rezerwuje tam 64px),
     wiec pasek zostaje w toku strony zamiast wjezdzac pod hamburgera. */
  @media (max-width: 768px) {
    position: static;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex: 1 1 240px;
  min-width: 0;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: var(--fs-xl);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text);
  min-width: 0;
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-left: auto;
  flex-wrap: wrap;
`;

const Intro = styled.p`
  margin: 0 0 var(--sp-4) 0;
  max-width: 70ch;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

const Notices = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);

  /* Bez tego pusty stos komunikatow zostawialby na stronie dziure na 16px. */
  &:empty {
    display: none;
    margin: 0;
  }
`;

// ── Uklad dwukolumnowy ──────────────────────────────────────────────────────
const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  /* align-items: start jest warunkiem dzialania sticky w prawej kolumnie — przy domyslnym
     stretch kolumna ma wysokosc calego wiersza i nie ma sie po czym przesuwac. */
  align-items: start;
  gap: var(--sp-6);

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sp-4);
  }
`;

const Panel = styled.section`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);
  padding: clamp(var(--sp-4), 2vw, var(--sp-5));
  box-sizing: border-box;
  min-width: 0;
`;

const PreviewPanel = styled(Panel)`
  @media (min-width: 1201px) {
    position: sticky;
    /* Tyle, ile zajmuje przyklejony pasek akcji. */
    top: 76px;
    max-height: calc(100vh - 96px);
    overflow-y: auto;
  }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin-bottom: var(--sp-4);
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 700;
  color: var(--text);
`;

const PanelTools = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  margin-left: auto;
  flex-wrap: wrap;
`;

/* Rama = biurko, w srodku kartka.
   Wlasne przewijanie WYLACZNIE ponizej progu, na ktorym prawa kolumna staje sie przyklejona
   (PreviewPanel nizej). Powyzej progu przewija sie caly panel, wiec ramka z drugim paskiem
   dawalaby dwa zagniezdzone obszary przewijania na tej samej tresci. */
const PreviewFrame = styled.div`
  padding: var(--sp-4);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--panel-bg);

  @media (max-width: 1200px) {
    max-height: min(70vh, 880px);
    overflow-y: auto;
  }
`;

export {
  Container,
  TopBar,
  TitleBlock,
  PageTitle,
  TopActions,
  Intro,
  Notices,
  MainGrid,
  Panel,
  PreviewPanel,
  PanelHead,
  PanelTitle,
  PanelTools,
  PreviewFrame,
  StatusStrip,
  StatusText,
  HistoryBox,
  HistoryHead,
  VersionCard,
  VersionDate,
  SheetNote,
  PublishButton,
  SmallButton,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalText,
  ModalActions,
  PdfModalBox,
  PdfModalHead,
  PdfDownloadLink,
  PdfFrame,
  PdfCloseButton,
};
